import React, { useEffect, useState, useRef } from 'react';
import { arrayOf, oneOfType, node, func, string, bool, number, object } from 'prop-types';
import styled from 'styled-components';
import Slider from 'react-slick';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { sendBenefitArrowClick, sendBenefitSwipe } from '../../services/tracking';

import ChevRight from './images/icons-system-chevron-right.svg';
import ChevLeft from './images/icons-system-chevron-left.svg';

const Container = styled.div`
  padding-bottom: 50px;
`;

const Card = styled.div`
  flex: 1 1 auto;
  background: #fff;
  padding: ${({ mobile }) => (mobile ? '24px' : '32px')};
  margin: ${({ mobile }) => (mobile ? '10px 4px' : '10px 12px')};
  border-radius: 8px;
  border: solid 1px #e5e5e5;
  width: 100%;
  /* max-width: 288px; */

  user-select: none;
  outline: 0;
`;

const SliderSlick = styled(Slider)`
  &&& .custom-dots {
    li {
      margin: 0px;
    }

    li button:hover:before {
      color: #6e0ad6;
      opacity: 0.25;
    }

    li.slick-active button::before {
      color: #6e0ad6;
      opacity: 0.75;
    }
  }

  &&& .slick-track {
    box-sizing: border-box;
    display: flex;
    flex-wrap: wrap;
  }

  &&& .slick-slide {
    height: inherit;
  }
  &&& .slick-slide > div {
    height: 100%;
    box-sizing: border-box;
    display: flex;
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  cursor: pointer;
  display: flex;
  font-size: 0;
  line-height: 0;
  padding: 0;
  color: transparent;
  background: transparent;
  outline: none;
  border: none;
  border-radius: 30px;
  background-color: #ffffff;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.3);
  z-index: 2;
  transform: ${({ mobile }) => (mobile ? 'translate(0, -40%)' : 'translate(0, -50%)')};
  top: ${({ mobile }) => (mobile ? '40%' : '50%')};
  width: ${({ mobile }) => (mobile ? '48px' : '56px')};
  height: ${({ mobile }) => (mobile ? '48px' : '56px')};
  
  background-position: center;
  transition: background 0.8s;

  &:hover {
    background: #ffffff radial-gradient(circle, transparent 1%, #ffffff 1%)
      center/15000%;
  }

  &:active {
    background-color: #D2D2D2;
    background-size: 100%;
    transition: background 0s;
  }
`;

const ChevImg = styled.img`
  margin: auto;
`;

const NextArrow = ({
  onClick, arrowDistance, mobile, posName, funnelId,
}) => (
  <ArrowButton
    mobile={mobile}
    style={{
      right: arrowDistance,
    }}
    onClick={() => {
      onClick();
      sendBenefitArrowClick(posName, 'right', funnelId);
    }}
  >
    <ChevImg src={ChevRight} />
  </ArrowButton>
);

const PrevArrow = ({
  onClick, arrowDistance, mobile, posName, funnelId,
}) => (
  <ArrowButton
    mobile={mobile}
    style={{
      left: arrowDistance,
    }}
    onClick={() => {
      onClick();
      sendBenefitArrowClick(posName, 'left', funnelId);
    }}
  >
    <ChevImg src={ChevLeft} />
  </ArrowButton>
);

const Carousel = ({
  children, settings: customSettings, mobile, externalIndex, posName, funnelId,
}) => {
  const slider = useRef();
  const [index, setIndex] = useState(externalIndex);

  let settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: true,
    dotsClass: 'slick-dots custom-dots',
    centerPadding: '15%',
    arrowDistance: 'calc(15% - 16px)',
    beforeChange: (current, next) => setIndex(next),
    onSwipe: direction => sendBenefitSwipe(posName, direction, funnelId),
  };

  useEffect(() => {
    if (slider && externalIndex !== index) {
      setTimeout(() => slider.current.slickGoTo(externalIndex), 300);
    }
  }, [externalIndex]);

  if (mobile) {
    settings = {
      ...settings,
      centerPadding: '15px',
      arrowDistance: '4px',
    };
  }

  settings = {
    ...settings,
    ...customSettings,
  };

  settings = {
    ...settings,
    nextArrow: <NextArrow arrowDistance={settings.arrowDistance} mobile={mobile} posName={posName} funnelId={funnelId} />,
    prevArrow: <PrevArrow arrowDistance={settings.arrowDistance} mobile={mobile} posName={posName} funnelId={funnelId} />,
  };

  return (
    <Container>
      <SliderSlick ref={slider} {...settings}>
        {React.Children.map(children, child => (
          <Card mobile={mobile}>{child}</Card>
        ))}
      </SliderSlick>
    </Container>
  );
};

export default Carousel;

PrevArrow.propTypes = {
  onClick: func,
  arrowDistance: string.isRequired,
  mobile: bool.isRequired,
  posName: string.isRequired,
  funnelId: string.isRequired,
};

NextArrow.propTypes = {
  onClick: func,
  arrowDistance: string.isRequired,
  mobile: bool.isRequired,
  posName: string.isRequired,
  funnelId: string.isRequired,
};

PrevArrow.defaultProps = {
  onClick: undefined,
};

NextArrow.defaultProps = {
  onClick: undefined,
};

Carousel.defaultProps = {
  settings: {},
  mobile: false,
  externalIndex: 0,
};

Carousel.propTypes = {
  children: oneOfType([
    arrayOf(node),
    node,
  ]).isRequired,
  settings: object,
  mobile: bool,
  externalIndex: number,
  posName: string.isRequired,
  funnelId: string.isRequired,
};
